const SUBSCRIPTION_PRODUCT_TAG = '定期便';
const subscriptionTagCache = new Map();

// カート内の商品が定期便商品かどうかを「定期便」タグで判定する。
// カートのJSONにはタグが含まれないため、商品ごとのJSONを取得して確認する。
// 判定結果はハンドル単位でキャッシュし、同一ページ内での再取得を避ける。
async function isSubscriptionCartItem(item) {
  if (item.selling_plan_allocation) return true;

  const handle = item.handle;
  if (!handle) return false;
  if (subscriptionTagCache.has(handle)) return subscriptionTagCache.get(handle);

  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const response = await fetch(`${root}products/${handle}.js`, {
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) throw new Error(`Product request failed: ${response.status}`);

  const product = await response.json();
  const hasTag = (product.tags || []).includes(SUBSCRIPTION_PRODUCT_TAG);
  subscriptionTagCache.set(handle, hasTag);
  return hasTag;
}

if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const formData = new FormData(this.form);
        const cartCompatibilityError = await this.getCartCompatibilityError(formData);
        if (cartCompatibilityError) {
          this.handleErrorMessage(cartCompatibilityError);
          this.dispatchCartErrorEvent(cartCompatibilityError, 'INVALID');
          this.submitButton.classList.remove('loading');
          this.submitButton.removeAttribute('aria-disabled');
          this.querySelector('.loading__spinner').classList.add('hidden');
          return;
        }

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(response.description || response.message));

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      async getCartCompatibilityError(formData) {
        // 定期便商品かどうかは「定期便」タグを正として判定する。
        // 銀行振込の申し込み（販売プランなし）も定期便商品として扱い、通常商品との混在を禁止する。
        // 販売プランの有無は、定期便の中での支払方法（カード自動決済か銀行振込か）の判定にのみ使う。
        const incomingIsSubscriptionProduct = this.dataset.subscriptionProduct === 'true';
        const incomingHasSellingPlan = Boolean(formData.get('selling_plan'));

        try {
          const response = await fetch(`${routes.cart_url}.json`, {
            headers: { Accept: 'application/json' },
          });
          if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);

          const cart = await response.json();
          const items = cart.items || [];
          const subscriptionFlags = await Promise.all(items.map(isSubscriptionCartItem));

          const cartHasSubscriptionProduct = subscriptionFlags.some(Boolean);
          const cartHasRegularProduct = subscriptionFlags.some((flag) => !flag);

          if (incomingIsSubscriptionProduct && cartHasRegularProduct) {
            return '定期便商品と通常商品は同時に購入できません。カート内の通常商品を削除してから、もう一度お試しください。';
          }

          if (!incomingIsSubscriptionProduct && cartHasSubscriptionProduct) {
            return '通常商品と定期便商品は同時に購入できません。カート内の定期便商品を削除してから、もう一度お試しください。';
          }

          // 定期便商品同士でも支払方法が異なるものは混在できない。
          // 販売プランを含むカートはチェックアウトでカード決済が強制されるため。
          if (incomingIsSubscriptionProduct) {
            const cartHasPlanItem = items.some((item) => Boolean(item.selling_plan_allocation));
            const cartHasBankTransferItem = items.some(
              (item, index) => subscriptionFlags[index] && !item.selling_plan_allocation
            );

            if (incomingHasSellingPlan && cartHasBankTransferItem) {
              return 'クレジットカードで自動決済の定期便と、銀行振込でお申し込みの定期便は同時に購入できません。カート内の銀行振込の商品を削除してから、もう一度お試しください。';
            }

            if (!incomingHasSellingPlan && cartHasPlanItem) {
              return '銀行振込でお申し込みの定期便と、クレジットカードで自動決済の定期便は同時に購入できません。カート内のクレジットカードの商品を削除してから、もう一度お試しください。';
            }
          }
        } catch (error) {
          console.error(error);
          return 'カートの内容を確認できませんでした。通信環境をご確認のうえ、もう一度お試しください。';
        }

        return '';
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
